import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import {
  getMissionDocumentSignedUrl,
  MISSION_BANNER_ASPECT,
  MISSION_IMAGE_BUCKET,
  normalizeBannerPan,
} from "utils/missionDocuments";

const BANNER_ACCEPT = "image/jpeg,image/jpg,image/png,image/gif,image/webp";
const DEFAULT_PAN = { x: 0.5, y: 0.5 };

function BannerFrame({ children, sx = {} }) {
  return (
    <SoftBox
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: `${MISSION_BANNER_ASPECT}`,
        borderRadius: "1rem",
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.05)"
            : "rgba(0,0,0,0.04)",
        ...sx,
      }}
    >
      {children}
    </SoftBox>
  );
}

BannerFrame.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};

function MissionBannerField({
  existingImage = null,
  idToken = "",
  file = null,
  pan = DEFAULT_PAN,
  onFileChange,
  onPanChange,
  onRemoveExisting,
  disabled = false,
}) {
  const frameRef = useRef(null);
  const dragRef = useRef(null);
  const [localPreview, setLocalPreview] = useState(null);
  const [savedPreview, setSavedPreview] = useState(null);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [naturalSize, setNaturalSize] = useState(null);
  const [dragging, setDragging] = useState(false);

  const { x: panX, y: panY } = normalizeBannerPan(pan);

  useEffect(() => {
    if (!file) {
      setLocalPreview(null);
      setNaturalSize(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setLocalPreview(url);
    const image = new Image();
    image.onload = () => {
      setNaturalSize({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };
    image.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (file || !existingImage?.fileName || !idToken) {
      setSavedPreview(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingSaved(true);
    getMissionDocumentSignedUrl(
      {
        fileName: existingImage.fileName,
        bucket: existingImage.bucket || MISSION_IMAGE_BUCKET,
        key: existingImage.fileName,
      },
      idToken,
    )
      .then((url) => {
        if (!cancelled) setSavedPreview(url);
      })
      .catch(() => {
        if (!cancelled) setSavedPreview(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingSaved(false);
      });
    return () => {
      cancelled = true;
    };
  }, [file, existingImage?.fileName, existingImage?.bucket, idToken]);

  const handleChange = (event) => {
    const next = event.target.files?.[0];
    event.target.value = "";
    if (!next) return;
    if (!String(next.type || "").startsWith("image/")) return;
    onFileChange?.(next);
  };

  const getOverflow = () => {
    const frame = frameRef.current;
    if (!frame || !naturalSize?.width || !naturalSize?.height) {
      return { extraX: 0, extraY: 0 };
    }
    const { width: fw, height: fh } = frame.getBoundingClientRect();
    if (!fw || !fh) return { extraX: 0, extraY: 0 };
    const scale = Math.max(fw / naturalSize.width, fh / naturalSize.height);
    return {
      extraX: Math.max(0, naturalSize.width * scale - fw),
      extraY: Math.max(0, naturalSize.height * scale - fh),
    };
  };

  const overflow = getOverflow();
  const canDrag = Boolean(file && !disabled && (overflow.extraX > 0 || overflow.extraY > 0));

  const applyDrag = (clientX, clientY) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = clientX - drag.startX;
    const dy = clientY - drag.startY;
    const nextX =
      drag.extraX > 0
        ? Math.min(1, Math.max(0, drag.startPanX - dx / drag.extraX))
        : drag.startPanX;
    const nextY =
      drag.extraY > 0
        ? Math.min(1, Math.max(0, drag.startPanY - dy / drag.extraY))
        : drag.startPanY;
    onPanChange?.({ x: nextX, y: nextY });
  };

  const handlePointerDown = (event) => {
    if (!file || disabled) return;
    const extras = getOverflow();
    if (extras.extraX <= 0 && extras.extraY <= 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startPanX: panX,
      startPanY: panY,
      extraX: extras.extraX,
      extraY: extras.extraY,
    };
    setDragging(true);
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current) return;
    applyDrag(event.clientX, event.clientY);
  };

  const endDrag = () => {
    dragRef.current = null;
    setDragging(false);
  };

  const objectPosition = `${panX * 100}% ${panY * 100}%`;
  const previewSrc = localPreview || savedPreview;

  return (
    <SoftBox>
      <SoftBox mb={0.5}>
        <SoftTypography variant="caption" fontWeight="bold">
          Mission banner
        </SoftTypography>
      </SoftBox>
      <SoftTypography variant="caption" color="secondary" display="block" mb={1}>
        This image is the header on the mission page. Choose a photo, then drag
        it to frame the part you want.
      </SoftTypography>
      <input
        type="file"
        accept={BANNER_ACCEPT}
        onChange={handleChange}
        style={{ display: "none" }}
        id="mission-banner-upload"
        disabled={disabled}
      />
      <SoftBox display="flex" gap={1} mb={1.5} flexWrap="wrap">
        <label htmlFor="mission-banner-upload">
          <SoftButton
            variant="outlined"
            color="info"
            component="span"
            disabled={disabled}
            startIcon={<Icon>add_photo_alternate</Icon>}
          >
            {previewSrc || existingImage?.fileName
              ? "Replace banner"
              : "Choose banner photo"}
          </SoftButton>
        </label>
        {(file || existingImage?.fileName) && (
          <SoftButton
            variant="text"
            color="error"
            disabled={disabled}
            onClick={() => {
              onFileChange?.(null);
              onRemoveExisting?.();
            }}
          >
            Remove
          </SoftButton>
        )}
      </SoftBox>
      <BannerFrame
        sx={{
          touchAction: canDrag ? "none" : "auto",
          cursor: canDrag ? (dragging ? "grabbing" : "grab") : "default",
          userSelect: "none",
        }}
      >
        <SoftBox
          ref={frameRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          sx={{ width: "100%", height: "100%" }}
        >
          {loadingSaved && !localPreview ? (
            <SoftBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <CircularProgress size={28} />
            </SoftBox>
          ) : previewSrc ? (
            <SoftBox
              component="img"
              src={previewSrc}
              alt="Mission banner preview"
              draggable={false}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition,
                display: "block",
                pointerEvents: "none",
              }}
            />
          ) : (
            <SoftBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              gap={0.5}
            >
              <Icon sx={{ fontSize: 40, color: "text.secondary" }}>wallpaper</Icon>
              <SoftTypography variant="caption" color="secondary">
                Banner preview (fits the mission header)
              </SoftTypography>
            </SoftBox>
          )}
        </SoftBox>
        {file ? (
          <IconButton
            size="small"
            onClick={() => onFileChange?.(null)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "background.paper",
              "&:hover": { bgcolor: "background.paper" },
            }}
          >
            <Icon sx={{ fontSize: 18 }}>close</Icon>
          </IconButton>
        ) : null}
      </BannerFrame>
      {file ? (
        <SoftTypography variant="caption" color="secondary" display="block" mt={1}>
          {canDrag
            ? "Drag the photo to crop it to the banner."
            : "This photo already fills the banner."}
        </SoftTypography>
      ) : null}
    </SoftBox>
  );
}

MissionBannerField.propTypes = {
  existingImage: PropTypes.object,
  idToken: PropTypes.string,
  file: PropTypes.object,
  pan: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
    }),
  ]),
  onFileChange: PropTypes.func,
  onPanChange: PropTypes.func,
  onRemoveExisting: PropTypes.func,
  disabled: PropTypes.bool,
};

export default MissionBannerField;
